import React from "react";
import { type NextPage } from "next";
import { Page } from "components/page";
import { StackedLogos } from "components/logos";
import { getLandingPageProps, getPageMetaImage, type LandingPageProps } from "utils";
import { Button } from "ui/button";
import { IndustryLeaderAwards } from "components/content";
import { Section } from "components/section";
import Image from "next/image";
import { Flex, Text } from "@chakra-ui/react";
import { Diptych } from "ui/diptych";
import { HightouchStats } from "components/about/hightouch-stats";
import { Investors } from "components/about/investors";
import { AboutHero } from "components/about/hero";
import foundersPhoto from "../../public/assets/company/about/founders-photo.png";

const About: NextPage<LandingPageProps> = ({ page, announcementBanner, defaultLogos, defaultMobileLogos }) => {
  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
      bgColor="new.yellow.150"
      footerOptions={{
        variant: "yellow",
        ctaProps: {
          title: "Join the data activation movement",
          description: "",
          actions: (
            <Button hasRightChevron href="/careers">
              Explore open positions
            </Button>
          ),
        },
      }}
    >
      <AboutHero title={page.title} description={page.description} />

      <Section my="0">
        <Diptych
          direction="right"
          media={<Image src={foundersPhoto} alt="Hightouch founders Tejas Manohar, Josh Curl, and Kashish Gupta." />}
          title="Founded with a vision"
          descriptionAs="div"
          description={
            <Flex flexDir="column" gap="small" textStyle="body3">
              <Text>
                Hightouch was founded by engineers who worked for years with customer data platforms and AI. They saw
                the transformative impact of enabling business and marketing teams to activate customer data. However,
                they also recognized the limitations of the existing rigid tools like legacy Customer Data Platforms.
              </Text>
              <Text>
                By building a platform that sits on top of an organization’s existing data infrastructure, our founders
                knew that teams could unlock a more performant, flexible and secure solution to activating customer data
                at scale — and thus, Hightouch was born.
              </Text>
              <Text>
                Now, Hightouch is dedicated to helping enterprises around the world leverage the most innovative
                technologies in AI and customer data to grow their business.
              </Text>
            </Flex>
          }
          actions={
            <Button hasRightChevron href="/careers">
              View open positions
            </Button>
          }
        />
      </Section>

      <StackedLogos
        customers={defaultLogos}
        mobileCustomers={defaultMobileLogos}
        title="We’re proud to serve customers that care about their customers"
      />

      <Section>
        <Diptych
          size="lg"
          direction="left"
          media={<HightouchStats />}
          title="Our technology ‘just works’ at any scale"
          titleAs="h2"
          description="We’ve built a product that can handle the scale of the largest enterprises’ most intense customer data use cases."
        />
      </Section>

      <Investors />

      <IndustryLeaderAwards />
    </Page>
  );
};

export const getStaticProps = getLandingPageProps("/about", {
  loadDefaultLogos: true,
  loadDefaultMobileLogos: true,
});

export default About;
